// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash/fp';

// Utils
import { stopPropagationOnly } from '../../utils/domUtil';
import { markEventAsHandled } from '../../utils/react/reactEventUtil';
import { isDomSelectionFocusInside } from '../../utils/dom/domSelectionUtils';
import { hasAltModifier, hasCommandModifier, hasShiftKey } from '../../utils/keyboard/keyboardUtility';
import { getIsSelectionCollapsed, placeCaretAtEnd, selectAll } from '../../utils/keyboard/contentEditable';
import { isPlatformIos } from '../../platform/utils/platformDetailsUtils';
import platformSingleton from '../../platform/platformSingleton';
import { handleBufferedCharacterInsertion } from './contentEditableHelpers';

// Components
import HighlightedText from '../HighlightedText';
import EditableTitleSearchHighlight from './EditableTitleSearchHighlight';

// Constants
import { KEY_CODES } from '../../utils/keyboard/keyConstants';

// Styles
import './ElementSimpleContentEditable.scss';

class ElementSimpleContentEditable extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            tempContent: null,
        };

        this.contentRef = React.createRef();
    }

    componentDidMount() {
        this.contentRef.current && this.contentRef.current.addEventListener('paste', this.handlePasteEvent);

        if (this.props.isEditing) this.selectContent();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { tempContent } = this.state;
        const { isEditing } = this.props;

        if (isEditing && !nextProps.isEditing) {
            this.updateContent(this.getContent());
            this.setState({ tempContent: null });
        }

        if (tempContent && !isEditing && nextProps.isEditing) {
            this.setState({ tempContent: null });
        }
    }

    componentDidUpdate(prevProps) {
        const { isEditing } = this.props;

        if (isEditing && !prevProps.isEditing) this.selectContent();
        if (!isEditing && prevProps.isEditing) this.blurContent();
    }

    componentWillUnmount() {
        if (!this.contentRef.current) return;
        this.contentRef.current.removeEventListener('paste', this.handlePasteEvent);

        if (this.props.isEditing) {
            this.updateContent(this.getContent());
        }
    }

    /**
     * Prevent inline styles and handle stop editing shortcuts.
     */
    onKeyDown = (event) => {
        // Prevent inline styles
        if (event.metaKey) {
            if (event.keyCode === KEY_CODES.B || event.keyCode === KEY_CODES.I) {
                event.preventDefault();
            }
        }

        const multilineShiftKey = this.props.multiline && hasShiftKey(event) && getIsSelectionCollapsed();
        const isEscapeKey = event.keyCode === KEY_CODES.ESC;

        // Stop editing on enter or escape
        if ((event.keyCode === KEY_CODES.ENTER && !multilineShiftKey) || isEscapeKey) {
            event.preventDefault();

            // Escape steps selection "out" one level: finish editing but keep the element selected.
            // Stop propagation so the global escape shortcut doesn't also deselect it on the same keystroke.
            if (isEscapeKey) event.stopPropagation();

            const keepSelection = isEscapeKey || hasShiftKey(event) || hasAltModifier(event) || hasCommandModifier(event);

            const content = this.getContent();
            this.setState({ tempContent: content });

            this.props.onStopEditing && this.props.onStopEditing({ content, keepSelection });
        }
        // Handle empty text backspace
        if (event.keyCode === KEY_CODES.BACKSPACE || event.keyCode === KEY_CODES.DELETE) {
            const content = this.getContent();

            if (!content.length) {
                this.props.onEmptyBackspace && this.props.onEmptyBackspace();
            }
        }
    };

    onMouseDown = (event) => {
        // If we're editing this component and the DOM selection is inside this component, then we want to tell the
        // parent component to not perform any mousedown handling because it's already been handled.
        // Previously we were using "event.stopPropagation()" for this purpose, however mousedown stopPropagations
        // result in weird bugs and glitches in the mouse-event DnD TouchBackend as it interferes with its state
        // updates.
        if (this.props.isEditing && isDomSelectionFocusInside(this.contentRef.current)) {
            markEventAsHandled(event);
        }
    };

    /**
     * Prevent double click from firing actions while it's being edited.
     */
    onDoubleClick = (event) => {
        if (this.props.isEditing) stopPropagationOnly(event);
    };

    getContent = () => this.cleanContent(get('innerText', this.contentRef.current)) || '';

    selectContent = () => {
        if (!this.contentRef.current) return;

        const { isInitialValue, selectAllOnEdit, replaceDefaultText, children } = this.props;

        // Handle any buffered characters that were typed before the editor was focused
        const bufferedCharsHandled = handleBufferedCharacterInsertion(this.contentRef.current, {
            replaceDefaultText,
            previousContent: children,
        });

        if (bufferedCharsHandled) {
            return;
        }

        // On iOS - editing after selecting all is a little painful on iOS due to being difficult to get
        //  rid of the selection. So we only select all on iOS if the content matches the initial value.
        const shouldSelectAll = selectAllOnEdit && (isInitialValue || !isPlatformIos(platformSingleton));

        shouldSelectAll ? selectAll(this.contentRef.current) : placeCaretAtEnd(this.contentRef.current);
    };

    blurContent = () => {
        this.contentRef.current && this.contentRef.current.blur();
    };

    updateContent = (content) => {
        const { isEditable, onUpdate } = this.props;

        if (!isEditable) return;

        if (!content.length && this.contentRef.current) this.contentRef.current.innerText = this.props.children;

        if (onUpdate) return onUpdate(content);
    };

    /**
     * Ensure the content is editable
     */
    startEditingContent = (event) => {
        const { isEditing, isEditable, selectFirst, isSelected, onStartEditing } = this.props;

        if (!isEditable) return;
        if (selectFirst && !isSelected) return;

        // stop propagation here so that the click event doesn't pass through to the parent &
        // trigger any click events. This is primarily to prevent the "double-click to enter" tooltip from
        // appearing when the user clicks on the editable title.
        event.stopPropagation();
        event.preventDefault();

        if (isEditing) return;

        const editorFocusRect = this.contentRef.current?.getBoundingClientRect();

        onStartEditing({ editorFocusRect });
    };

    handlePasteEvent = (event) => {
        // cancel paste
        event.preventDefault();

        // get text representation of clipboard
        const text = (event.originalEvent || event).clipboardData.getData('text/plain');

        // insert text manually
        document.execCommand('insertHTML', false, this.cleanContent(text));
    };

    cleanContent = (content) => {
        const { cleanContent } = this.props;

        if (!content || !cleanContent) return content;

        return cleanContent(content);
    };

    render() {
        const { tempContent } = this.state;
        const { children, isEditing, isEditable, wrapContent, filterQuery, className, enterKeyHint, multiline } =
            this.props;

        const showEditing = isEditable && isEditing;
        const visibleContent = tempContent || children;
        const cleanedContent = this.cleanContent(visibleContent);
        const content = showEditing ? (
            cleanedContent
        ) : (
            // FIXME Externalise the HighlightComponent
            <HighlightedText
                className="title-text"
                highlightText={filterQuery}
                text={cleanedContent}
                HighlightComponent={EditableTitleSearchHighlight}
            />
        );

        return (
            <div
                className={classNames('ElementSimpleContentEditable', { editing: showEditing, multiline }, className)}
                id={this.props.domElementId}
                ref={this.contentRef}
                onClick={this.startEditingContent}
                onBlur={this.props.onBlur}
                contentEditable={showEditing}
                enterKeyHint={enterKeyHint}
                suppressContentEditableWarning
                onKeyDown={this.onKeyDown}
                onMouseDown={this.onMouseDown}
                onDoubleClick={this.onDoubleClick}
            >
                {wrapContent && !isEditing ? React.cloneElement(wrapContent, null, content) : content}
            </div>
        );
    }
}

ElementSimpleContentEditable.propTypes = {
    className: PropTypes.string,
    // This is the content
    children: PropTypes.string,

    enterKeyHint: PropTypes.string,
    filterQuery: PropTypes.string,

    isInitialValue: PropTypes.bool,
    isEditing: PropTypes.bool,
    isEditable: PropTypes.bool,
    isSelected: PropTypes.bool,
    selectAllOnEdit: PropTypes.bool,
    selectFirst: PropTypes.bool,
    multiline: PropTypes.bool,
    replaceDefaultText: PropTypes.bool,
    domElementId: PropTypes.string,

    onUpdate: PropTypes.func,
    onStartEditing: PropTypes.func,
    onStopEditing: PropTypes.func,
    onEmptyBackspace: PropTypes.func,
    onBlur: PropTypes.func,

    cleanContent: PropTypes.func,
    wrapContent: PropTypes.element,
};

export default ElementSimpleContentEditable;
